/* eslint eslint-comments/no-use: off */
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {Link} from '@primer/react'
import {Suspense, useCallback} from 'react'
import {graphql, useLazyLoadQuery, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {commitUnArchiveProjectItem} from '../../mutations/unarchive-project-item'
import type {ProjectItemSectionFieldListQuery} from './__generated__/ProjectItemSectionFieldListQuery.graphql'
import fieldListStyles from './ProjectItemSectionFieldList.module.css'
import type {ProjectItemSectionFieldsFragment} from './ProjectItemSectionFields'
import {ProjectItemSectionFields} from './ProjectItemSectionFields'
import {ProjectItemSectionFieldsLoading} from './ProjectItemSectionFieldsLoading'
import styles from './ProjectsSections.module.css'

export type ProjectItemSectionFieldListProps = {
  projectId: string
  projectItemId: string
  isArchived: boolean
  onIssueUpdate: () => void
}

export function ProjectItemSectionFieldList(props: ProjectItemSectionFieldListProps) {
  return (
    <Suspense fallback={<ProjectItemSectionFieldsLoading />}>
      <ProjectItemSectionFieldListInternal {...props} />
    </Suspense>
  )
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const ProjectItemSectionFieldListGraphqlQuery = graphql`
  query ProjectItemSectionFieldListQuery($id: ID!) {
    node(id: $id) {
      ... on ProjectV2Item {
        id
        isArchived
        project {
          viewerCanUpdate
          url
          id
          title
          fields(first: 50, orderBy: {field: POSITION, direction: ASC}) {
            edges {
              node {
                ... on ProjectV2Field {
                  id
                  dataType
                  name
                  isIssueField
                }
                ... on ProjectV2IterationField {
                  id
                  dataType
                  name
                  isIssueField
                  configuration {
                    iterations {
                      id
                      title
                      titleHTML
                      startDate
                      duration
                    }
                    completedIterations {
                      id
                      title
                      titleHTML
                      startDate
                      duration
                    }
                  }
                }
                ... on ProjectV2SingleSelectField {
                  id
                  dataType
                  name
                  isIssueField
                  options {
                    id
                    optionId
                    name
                    nameHTML
                    color
                    descriptionHTML
                    description
                  }
                }
                ... on ProjectV2MultiSelectField {
                  id
                  dataType
                  name
                  isIssueField
                  # eslint-disable-next-line relay/must-colocate-fragment-spreads
                  ...MultiSelectFieldConfigFragment @alias(as: "multiSelectFieldConfig")
                }
              }
            }
          }
        }
        fieldValues(first: 50, orderBy: {field: POSITION, direction: ASC})
          @connection(key: "ProjectItemSection_fieldValues") {
          edges {
            node {
              ... on ProjectV2ItemFieldTextValue {
                id
                text
                field {
                  ... on ProjectV2Field {
                    id
                  }
                }
              }
              ... on ProjectV2ItemFieldNumberValue {
                id
                number
                field {
                  ... on ProjectV2Field {
                    id
                  }
                }
              }
              ... on ProjectV2ItemFieldDateValue {
                id
                date
                field {
                  ... on ProjectV2Field {
                    id
                  }
                }
              }
              ... on ProjectV2ItemFieldSingleSelectValue {
                id
                field {
                  ... on ProjectV2SingleSelectField {
                    id
                  }
                }
                optionId
                name
                nameHTML
                color
              }
              ... on ProjectV2ItemFieldIterationValue {
                id
                iterationId
                title
                titleHTML
                startDate
                duration
                field {
                  ... on ProjectV2IterationField {
                    id
                    name
                    configuration {
                      iterations {
                        id
                        title
                        titleHTML
                        startDate
                        duration
                      }
                      completedIterations {
                        id
                        title
                        titleHTML
                        startDate
                        duration
                      }
                    }
                  }
                }
              }
              ... on ProjectV2ItemFieldMultiSelectValue {
                id
                field {
                  ... on ProjectV2MultiSelectField {
                    id
                  }
                }
              }
              # eslint-disable-next-line relay/must-colocate-fragment-spreads
              ...MultiSelectFieldFragment @alias(as: "multiSelectFieldValue")
            }
          }
        }
      }
    }
  }
`

function ProjectItemSectionFieldListInternal({
  isArchived,
  projectItemId,
  projectId,
  onIssueUpdate,
}: ProjectItemSectionFieldListProps) {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const {node} = useLazyLoadQuery<ProjectItemSectionFieldListQuery>(
    ProjectItemSectionFieldListGraphqlQuery,
    {id: projectItemId},
    {networkCacheConfig: {metadata: {ttl: DEFAULT_RELAY_CACHE_TTL}}},
  )
  const restore = useCallback(() => {
    commitUnArchiveProjectItem({
      environment,
      input: {projectId, itemId: projectItemId},
      onCompleted: () => {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({type: 'success', message: LABELS.archivedSuccess})
      },
      onError: () => {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({type: 'error', message: LABELS.archivedError})
      },
    })
  }, [environment, projectId, projectItemId, addToast])

  if (!node || !node.project) return null
  const canRestore = isArchived && node.project.viewerCanUpdate

  return (
    <>
      {canRestore && (
        <ul className={styles.FieldListUl}>
          <li className={styles.ArchivedLi}>
            <span className={styles.FieldListLiText}>
              {LABELS.archivedDescription}
              &nbsp;
              <Link onClick={restore} className={fieldListStyles.restoreLink}>
                Restore
              </Link>
            </span>
          </li>
        </ul>
      )}
      <ProjectItemSectionFields onIssueUpdate={onIssueUpdate} projectItem={node as ProjectItemSectionFieldsFragment} />
    </>
  )
}
